import { Button, Card, CardContent, CardHeader, CardTitle, cn } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { ChevronDown, Play, Power, Snowflake, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { EXCHANGE_STATUS_QUERY_KEY, useExchangeStatus } from '@/hooks/queries/exchange/use-exchange-status';
import { freezeExchange, killExchange, unfreezeExchange } from '@/services/api/markets';
import { FreezeConfirmDialog, KillConfirmDialog, KillVerificationDialog, UnfreezeConfirmDialog } from './exchange-action-dialogs';

const HTTP_FORBIDDEN = 403;

// Copy verbatim from the legacy ExchangeControls
const FREEZE_CONSEQUENCES = [
  'All coin pairs will be suspended.',
  'No new orders can be placed.',
  'Open orders can still be canceled.',
  'No trades will happen.',
  'No settlements will be paid.',
  'The exchange will remain in this frozen state until manually unfrozen.',
];

const KILL_CONSEQUENCES = [
  'All open orders will be canceled.',
  'The exchange application will be stopped.',
  'No new orders can be placed or canceled.',
  'No trades will happen.',
  'The funding rate will freeze.',
  'No settlements will be paid.',
  'No withdrawals or deposits will be processed.',
  'Restarting the exchange will take significant time.',
];

type DialogStage = 'freeze-confirm' | 'unfreeze-confirm' | 'kill-confirm' | 'kill-verify' | null;

const ConsequencesList = ({ items }: { items: string[] }) => (
  <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);
ConsequencesList.displayName = 'ConsequencesList';

const ExchangeControls = () => {
  const queryClient = useQueryClient();
  // Deliberate fix: one TanStack query (same key as the maintenance panel)
  // shared by the status card and these controls — legacy fetched twice on mount.
  const { data: status } = useExchangeStatus();
  const [stage, setStage] = useState<DialogStage>(null);

  const refetchStatus = () => queryClient.invalidateQueries({ queryKey: EXCHANGE_STATUS_QUERY_KEY });

  const closeStage = () => setStage(null);

  // Freeze flow stage 2 — the shared audit-justification dialog
  const runFreezeJustification = () => {
    closeStage();
    void confirmAction({
      title: 'Freeze Exchange',
      description: 'Please provide a justification for freezing the exchange. This action will be logged and is auditable.',
      actionLabel: 'Freeze',
      onConfirm: async (comment) => {
        try {
          // Key is `comment`, not `justification` — the Nuxt BFF used to rename it
          await freezeExchange(comment);
          toast.success('Exchange frozen successfully');
        } catch {
          toast.error('Failed to freeze exchange');
        }
        await refetchStatus();
      },
    });
  };

  const runUnfreezeJustification = () => {
    closeStage();
    void confirmAction({
      title: 'Unfreeze Exchange',
      description: 'Please provide a justification for unfreezing the exchange. This action will be logged and is auditable.',
      actionLabel: 'Unfreeze',
      onConfirm: async (comment) => {
        try {
          await unfreezeExchange(comment);
          toast.success('Exchange unfrozen successfully');
        } catch {
          toast.error('Failed to unfreeze exchange');
        }
        await refetchStatus();
      },
    });
  };

  // Killswitch flow stage 3
  const runKillJustification = () => {
    closeStage();
    void confirmAction({
      title: 'Kill Exchange',
      description: 'Please provide a justification for killing the exchange. This is a critical action that will be logged and is auditable.',
      actionLabel: 'Kill',
      destructive: true,
      onConfirm: async (comment) => {
        try {
          await killExchange(comment);
          toast.success('Exchange killed successfully');
        } catch (error) {
          // Deliberate fix: legacy collapsed the server allowlist 403
          // into a generic failure — surface "not authorised" explicitly.
          if (error instanceof HTTPError && error.response.status === HTTP_FORBIDDEN) {
            toast.error('You are not authorised to kill the exchange — only exchange admins may do this');
          } else {
            toast.error('Failed to kill exchange');
          }
        }
        await refetchStatus();
      },
    });
  };

  const isDisabledStatus = status === 'disabled';
  // Buttons stay unusable until the status is known — every action derives from it
  const isStatusUnknown = status === undefined;

  return (
    <div className="flex flex-col gap-4">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Freeze Exchange</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">Clicking this button and confirming will suspend the entire exchange. This means:</p>
          <ConsequencesList items={FREEZE_CONSEQUENCES} />
          <div>
            {status === 'frozen' ? (
              <Button className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setStage('unfreeze-confirm')} type="button">
                <Play className="size-4" />
                Unfreeze Exchange
              </Button>
            ) : (
              // Visible but unclickable whenever the killswitch is active
              <Button
                className={cn('gap-2 bg-amber-500 text-white hover:bg-amber-600', isDisabledStatus && 'pointer-events-none opacity-50')}
                disabled={isDisabledStatus || isStatusUnknown}
                onClick={() => setStage('freeze-confirm')}
                type="button"
              >
                <Snowflake className="size-4" />
                Freeze Exchange
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Killswitch</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">Clicking this button and confirming will completely shut down the exchange. This means:</p>
          <ConsequencesList items={KILL_CONSEQUENCES} />
          <p className="text-sm font-medium text-red-500">Use this only in critical situations, as recovery is not immediate.</p>
          <div>
            <Button className="gap-2" disabled={isDisabledStatus || isStatusUnknown} onClick={() => setStage('kill-confirm')} type="button" variant="destructive">
              <Power className="size-4" />
              Kill Exchange
            </Button>
          </div>
        </CardContent>
      </Card>

      {stage === 'freeze-confirm' ? <FreezeConfirmDialog onCancel={closeStage} onContinue={runFreezeJustification} /> : null}
      {stage === 'unfreeze-confirm' ? <UnfreezeConfirmDialog onCancel={closeStage} onContinue={runUnfreezeJustification} /> : null}
      {stage === 'kill-confirm' ? <KillConfirmDialog onCancel={closeStage} onContinue={() => setStage('kill-verify')} onSwitchToFreeze={() => setStage('freeze-confirm')} /> : null}
      {stage === 'kill-verify' ? <KillVerificationDialog onCancel={closeStage} onContinue={runKillJustification} /> : null}
    </div>
  );
};
ExchangeControls.displayName = 'ExchangeControls';

/**
 * Collapsed-by-default accordion holding the exchange controls —
 * the only way to reach freeze/kill. Contents mount on first expand, which is
 * when the status query fires.
 */
export const DangerZone = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section aria-label="Danger Zone" className="rounded-md border border-red-500/40">
      <button
        aria-expanded={isExpanded}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={() => setIsExpanded((previous) => !previous)}
        type="button"
      >
        <span className="flex items-center gap-2 font-semibold text-red-500">
          <TriangleAlert className="size-4" />
          Danger Zone
        </span>
        <ChevronDown className={cn('size-4 text-muted-foreground transition-transform', isExpanded && 'rotate-180')} />
      </button>
      {isExpanded ? (
        <div className="border-t border-red-500/40 p-4">
          <ExchangeControls />
        </div>
      ) : null}
    </section>
  );
};
DangerZone.displayName = 'DangerZone';
